import type { FC, ReactNode, SVGProps } from 'react'
import type { Product, ProductColor, WishlistItem } from '@/types'

import { Heart, Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { stockLabel } from '@/lib/format'
import { useShop } from '@/components/providers/shop-provider'
import { labelName } from '@/constants/site'

interface ProductBadgesProps {
    product: Pick<Product, 'id' | 'labels' | 'badge'>
    className?: string
    badgeClassName?: string
    limit?: number
}

// The product's own badge text (editable in the CMS) followed by its labels.
export const ProductBadges: FC<ProductBadgesProps> = ({ product, className, badgeClassName, limit = 3 }): ReactNode => {
    const labels = product.labels.slice(0, Math.max(0, limit - (product.badge ? 1 : 0)))

    if (!product.badge && !labels.length) return null

    const pill = cn('rounded-full bg-cream-50/90 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-cocoa-800 shadow-sm backdrop-blur', badgeClassName)

    return (
        <span className={cn('flex flex-wrap items-start gap-1.5', className)}>
            {product.badge && (
                <span className={pill}>
                    <span data-mdlfy-cms={`products:${product.id}:badge`}>
                        {product.badge}
                    </span>
                </span>
            )}
            {labels.map(label => (
                <span key={label} className={cn(pill, label === 'edisi-terbatas' && 'bg-cocoa-800/90 text-cream-50', label === 'terlaris' && 'bg-blush-100/95 text-blush-600')}>
                    {labelName(label)}
                </span>
            ))}
        </span>
    )
}

// WhatsApp-style chat glyph (drawn for this site, sized like lucide icons).
export const WhatsAppIcon: FC<SVGProps<SVGSVGElement>> = ({ className, ...props }): ReactNode => {
    return (
        <svg viewBox='0 0 24 24' fill='none' aria-hidden='true' className={cn('size-5', className)} {...props}>
            <path
                d='M12 2.75a9.25 9.25 0 0 0-7.98 13.93L2.75 21.25l4.7-1.23A9.25 9.25 0 1 0 12 2.75Z'
                stroke='currentColor'
                strokeWidth='1.7'
                strokeLinejoin='round'
            />
            <path
                d='M8.7 7.6c.2-.4.4-.45.65-.45h.5c.17 0 .38.02.52.37l.72 1.72c.08.2.07.4-.04.58l-.45.66c-.1.15-.1.33 0 .49.4.68.92 1.3 1.55 1.83.6.5 1.2.87 1.86 1.15.17.07.36.03.48-.1l.62-.74c.15-.17.37-.22.58-.12l1.66.8c.2.1.36.2.36.42 0 .43-.12 1.03-.55 1.45-.43.42-1.12.83-1.86.83-1.02 0-2.4-.42-4-1.62-1.87-1.4-3.08-3.18-3.43-4.06-.37-.92-.38-1.62-.1-2.3.2-.48.5-.86.63-1.11Z'
                fill='currentColor'
            />
        </svg>
    )
}

interface StockBadgeProps {
    stock: number
    madeToOrder: boolean
    className?: string
}

export const StockBadge: FC<StockBadgeProps> = ({ stock, madeToOrder, className }): ReactNode => {
    const { tone } = stockLabel(stock, madeToOrder)

    return (
        <span className={cn('inline-flex items-center gap-2 text-sm font-semibold', className)}>
            <span
                aria-hidden='true'
                className={cn(
                    'size-2 rounded-full',
                    tone === 'ok' && 'bg-sage-500',
                    tone === 'low' && 'bg-caramel-400 animate-pulse',
                    tone === 'out' && 'bg-cocoa-500',
                    tone === 'custom' && 'bg-blush-500'
                )}
            />
            <span className={cn(tone === 'out' ? 'text-cocoa-600' : 'text-cocoa-700')}>
                {tone === 'custom' && 'Dibuat sesuai pesanan'}
                {tone === 'out' && 'Stok Habis'}
                {tone === 'low' && <>Tersedia, sisa <span>{stock}</span></>}
                {tone === 'ok' && <>Tersedia, stok <span>{stock}</span></>}
            </span>
        </span>
    )
}

interface ColorSwatchesProps {
    colors: Array<ProductColor>
    value: number
    onChange: (index: number) => void
    size?: 'sm' | 'md'
    label?: string
}

export const ColorSwatches: FC<ColorSwatchesProps> = ({ colors, value, onChange, size = 'md', label = 'Warna' }): ReactNode => {
    if (!colors.length) return null

    const selected = colors[Math.min(value, colors.length - 1)]

    return (
        <div>
            <p className='mb-2.5 text-sm text-muted-foreground'>
                {label}
                {': '}
                <span className='font-semibold text-foreground'>
                    {selected.label}
                </span>
            </p>
            <div role='radiogroup' aria-label={label} className='flex flex-wrap gap-2.5'>
                {colors.map((color, index) => {
                    const active = index === value

                    return (
                        <button
                            key={`${color.label}-${index}`}
                            type='button'
                            role='radio'
                            aria-checked={active}
                            aria-label={color.label}
                            title={color.label}
                            onClick={() => onChange(index)}
                            className={cn(
                                'relative rounded-full border border-black/10 transition-transform duration-200 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-caramel-400 focus-visible:ring-offset-2',
                                size === 'md' ? 'size-9' : 'size-7',
                                active && 'ring-2 ring-cocoa-700 ring-offset-2 ring-offset-background'
                            )}
                            style={{ background: color.hex ?? 'linear-gradient(135deg, #f3c9cf, #a9ba9d)' }}
                        />
                    )
                })}
            </div>
        </div>
    )
}

interface QuantitySelectorProps {
    value: number
    max: number | null
    onChange: (value: number) => void
    size?: 'sm' | 'md'
    label?: string
}

export const QuantitySelector: FC<QuantitySelectorProps> = ({ value, max, onChange, size = 'md', label = 'Jumlah' }): ReactNode => {
    const limit = max && max > 0 ? Math.min(max, 20) : 20

    return (
        <div
            role='group'
            aria-label={label}
            className={cn('inline-flex items-center rounded-full border border-border bg-card', size === 'md' ? 'h-12' : 'h-9')}
        >
            <button
                type='button'
                aria-label='Kurangi jumlah'
                disabled={value <= 1}
                onClick={() => onChange(Math.max(1, value - 1))}
                className={cn('grid place-items-center rounded-full text-cocoa-700 transition-colors hover:bg-muted disabled:opacity-40', size === 'md' ? 'size-12' : 'size-9')}
            >
                <Minus className='size-4' />
            </button>
            <span aria-live='polite' className={cn('min-w-8 text-center font-semibold tabular-nums', size === 'md' ? 'text-base' : 'text-sm')}>
                {value}
            </span>
            <button
                type='button'
                aria-label='Tambah jumlah'
                disabled={value >= limit}
                onClick={() => onChange(Math.min(limit, value + 1))}
                className={cn('grid place-items-center rounded-full text-cocoa-700 transition-colors hover:bg-muted disabled:opacity-40', size === 'md' ? 'size-12' : 'size-9')}
            >
                <Plus className='size-4' />
            </button>
        </div>
    )
}

interface WishlistButtonProps {
    item: WishlistItem
    className?: string
    variant?: 'icon' | 'pill'
}

export const WishlistButton: FC<WishlistButtonProps> = ({ item, className, variant = 'icon' }): ReactNode => {
    const { isWishlisted, toggleWishlist, hydrated } = useShop()
    const active = hydrated && isWishlisted(item.productId)

    if (variant === 'pill') {
        return (
            <button
                type='button'
                aria-pressed={active}
                onClick={() => toggleWishlist(item)}
                className={cn(
                    'inline-flex h-12 items-center justify-center gap-2 rounded-full border border-border bg-card px-5 text-sm font-semibold text-cocoa-700 transition-colors hover:border-blush-300 hover:bg-blush-50',
                    active && 'border-blush-300 bg-blush-50',
                    className
                )}
            >
                <Heart className={cn('size-5 transition-colors', active ? 'fill-blush-500 text-blush-500' : 'text-cocoa-600')} />
                {active ? 'Favorit' : 'Simpan'}
            </button>
        )
    }

    return (
        <button
            type='button'
            aria-pressed={active}
            aria-label={active ? `Hapus ${item.name} dari favorit` : `Simpan ${item.name} ke favorit`}
            onClick={() => toggleWishlist(item)}
            className={cn(
                'grid size-10 place-items-center rounded-full bg-white/85 text-cocoa-700 shadow-sm backdrop-blur transition hover:scale-105 hover:bg-white',
                className
            )}
        >
            <Heart className={cn('size-5 transition-colors', active ? 'fill-blush-500 text-blush-500' : '')} />
        </button>
    )
}
